// src/pages/StackedLaserCutLayers2.js
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { initializeThreeScene } from "../utils/initializeThreeScene";
import useVisualHelpers from "../hooks/useVisualHelpers";
import TutorialWindow from "../components/TutorialWindow";
import { generateSVG, downloadSVG } from "../utils/exportUtils";
import { stackedLayersTutorial } from "../utils/tutorialContents";
import { useSyncedCamera } from '../hooks/useSyncedCamera';
import { sliceModelXZ } from "../utils/sliceModel";
import { normalizeModel } from "../utils/normalizeModel";
import { MACHINE_PRESETS } from "../components/Sidebar";
import "../styles/common.css";

const getSelectedLaserPreset = () => {
  const selected =
    window.labConstraints?.machineSettings?.laserCutter?.selected ||
    "Trotec Speedy 400";
  const bed =
    MACHINE_PRESETS.laserCutter[selected] ||
    MACHINE_PRESETS.laserCutter["Trotec Speedy 400"];
  return { selected, bed };
};

const StackedLaserCutLayers2 = ({ stlFile }) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const objRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [modelDimensions, setModelDimensions] = useState(null);
  const [error, setError] = useState(null);
  const [svgReady, setSvgReady] = useState(true);
  const [processingStatus, setProcessingStatus] = useState("");
  const [tutorialVisible, setTutorialVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [constraintWarnings, setConstraintWarnings] = useState([]);
const [laserCutterSettings, setLaserCutterSettings] = useState({
  selected: 'Trotec Speedy 400', // 修改这里
  expanded: false
});
  // 估算结果
const [estimatedCutTimeSec, setEstimatedCutTimeSec] = useState(null);
const [pathStats, setPathStats] = useState({ mmTotal: 0, contours: 0, layers: 0 });

  const pageId = useMemo(() => `stacked-layers-2`, []);
  useSyncedCamera(pageId, cameraRef, controlsRef);
  const { createVisualization, removeVisualHelpers } = useVisualHelpers();

  const shapesToExport = [];
  const layerLines = [];
const formatMm = (mm) => `${Math.round(mm)} mm`;
const formatTimeS = (s) => {
  if (s == null) return "-";
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}m ${sec}s`;
};

  // 当前材质：'wood' 或 'acrylic'
  const [materialKey, setMaterialKey] = useState('wood');

  // 复用材质实例
  const woodMaterial = useMemo(() => (
    new THREE.MeshPhongMaterial({
      color: 0xb98f71, side: THREE.DoubleSide, shininess: 30
    })
  ), []);

  const acrylicMaterial = useMemo(() => (
    new THREE.MeshPhongMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.35,
      side: THREE.DoubleSide,
      shininess: 80
    })
  ), []);

  // 当前材质引用
  const currentMaterialRef = useRef(woodMaterial);
  useEffect(() => {
    currentMaterialRef.current = (materialKey === 'acrylic') ? acrylicMaterial : woodMaterial;
  }, [materialKey, woodMaterial, acrylicMaterial]);

  // 记录所有 extruded 层
  const extrudedMeshesRef = useRef([]);

  const applyMaterialToLayers = useCallback((mat) => {
    extrudedMeshesRef.current.forEach(m => {
      m.material = mat;
      m.material.needsUpdate = true;
    });
  }, []);

  // 添加激光切割机尺寸检查函数
// 替换原来的 checkLaserCutterConstraints
const checkLaserCutterConstraints = useCallback((bboxOrNull) => {
  const bbox = bboxOrNull || (objRef.current ? new THREE.Box3().setFromObject(objRef.current) : null);
  if (!bbox) return { ok: true };

  const { selected, bed } = getSelectedLaserPreset();

  const size = bbox.getSize(new THREE.Vector3());
  // Laser bed plane is X (width) × Z (height/depth). Y is the vertical stack.
  const modelW = size.x;
  const modelH = size.z; // ← was size.y

  const tooWide = modelW > bed.width;
  const tooTall = modelH > bed.height;

  if (tooWide || tooTall) {
    setConstraintWarnings([{
      type: "size_exceeded",
      layer: "Model",
      cutter: selected,                    // ← add this (UI expects `cutter`)
      currentSize: { width: modelW.toFixed(1), height: modelH.toFixed(1) },
      maxSize: { width: bed.width, height: bed.height },
      message: `Model ${modelW.toFixed(1)}×${modelH.toFixed(1)} mm exceeds bed ${bed.width}×${bed.height} mm`
    }]);
    return { ok: false };
  }

  
  setConstraintWarnings([]);
  return { ok: true };
}, []);

  const createShapeFromHorizontalComponent = (uniqueVertices, compSegs) => {
    const indexSet = new Set();
    for (const seg of compSegs) {
      indexSet.add(seg.i1);
      indexSet.add(seg.i2);
    }
    const indices = [...indexSet];
    if (indices.length < 3) return null;
    const points = indices.map(i => new THREE.Vector2(uniqueVertices[i].x, uniqueVertices[i].z));
    const centroid = points.reduce((acc, p) => acc.add(p), new THREE.Vector2()).divideScalar(points.length);
    points.sort((a, b) => Math.atan2(a.y - centroid.y, a.x - centroid.x) - Math.atan2(b.y - centroid.y, b.x - centroid.x));
    const shape = new THREE.Shape();
    shape.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) shape.lineTo(points[i].x, points[i].y);
    shape.lineTo(points[0].x, points[0].y);
    return shape;
  };

  const bakeHorizontalShapeToWorld = (shape, matrixWorld) => {
    const points = shape.getPoints();
    const bakedPoints = points.map(pt => {
      const vec = new THREE.Vector3(pt.x, 0, pt.y).applyMatrix4(matrixWorld);
      return new THREE.Vector2(vec.x, vec.z);
    });
    return new THREE.Shape(bakedPoints);
  };

  const addHorizontalShape = (shape, extrudeSettings, pos, rot, scl) => {
    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    const mesh = new THREE.Mesh(geometry, currentMaterialRef.current);
    mesh.setRotationFromEuler(new THREE.Euler(Math.PI/2, 0, 0));
    mesh.position.copy(pos);
    if (scl) mesh.scale.copy(scl);
    mesh.updateMatrixWorld();
    return mesh;
  };

  const generateHorizontalStackedLayers = (obj, scene) => {

    // 生成前清空，避免重复累计
shapesToExport.length = 0;

    const bbox = new THREE.Box3().setFromObject(obj);
    const size = bbox.getSize(new THREE.Vector3());
    const layerHeight = 3; // 每层厚度（mm）
    const count = Math.ceil(size.y / layerHeight);
    const extrudedMeshes = [];
    
    for (let i = 0; i < count; i++) {
      const y = bbox.min.y + i * layerHeight;
      const { uniqueVertices, componentSegments } = sliceModelXZ(obj, scene, y);
      if (!uniqueVertices || !componentSegments) continue;
      
      componentSegments.forEach(segs => {
        if (segs.length < 3) return;
        const shape = createShapeFromHorizontalComponent(uniqueVertices, segs);
        if (!shape) return;
        
        const extruded = addHorizontalShape(shape, {
          depth: -3, 
          bevelEnabled: true, 
          steps: 2, 
          bevelSize: 0, 
          bevelThickness: 0, 
          bevelSegments: 2
        }, new THREE.Vector3(obj.position.x, y, obj.position.z), obj.rotation, obj.scale);
        
        scene.add(extruded);
        extrudedMeshesRef.current.push(extruded);
        
        const line = new THREE.Line(
          new THREE.BufferGeometry().setFromPoints(shape.getPoints()), 
          new THREE.LineBasicMaterial({ color: 0x000000 })
        );
        extruded.add(line);
        layerLines.push(line);
        shapesToExport.push(bakeHorizontalShapeToWorld(shape, extruded.matrixWorld));
        extrudedMeshes.push(extruded);
      });
    }
    return { shapesToExport, extrudedMeshes, layerLines };
  };
const estimateLaserTime = useCallback((shapes, layerCount) => {
  // 粗略常量，可按材料/机器微调
  const CUT_SPEED_MM_S = 25;    // 切割速度（3mm 木板的常见量级）
  const TRAVEL_SPEED_MM_S = 150; // 空程速度
  const PIERCE_SEC = 0.12;       // 每个轮廓起弧/穿孔耗时
  const FIXED_OVERHEAD_SEC = 12; // 开机/对焦/空气辅助等固定耗时

  // 计算所有轮廓总周长（世界坐标下，单位≈mm）
  let mmTotal = 0;
  let contours = 0;

  const segLen = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);

  for (const shape of shapes) {
    const pts = shape.getPoints();         // THREE.Vector2[]
    if (!pts || pts.length < 2) continue;
    contours++;
    for (let i = 1; i < pts.length; i++) {
      mmTotal += segLen(pts[i - 1], pts[i]);
    }
    // 闭合
    if (pts.length > 2 &&
        (pts[0].x !== pts[pts.length - 1].x || pts[0].y !== pts[pts.length - 1].y)) {
      mmTotal += segLen(pts[pts.length - 1], pts[0]);
    }
  }

  // 简单时间模型：切割 + (约12%)空程 + 每轮廓起弧 + 固定开销
  const cutTime = mmTotal / CUT_SPEED_MM_S;
  const travelTime = (mmTotal * 0.12) / TRAVEL_SPEED_MM_S;
  const pierceTime = contours * PIERCE_SEC;
  const total = cutTime + travelTime + pierceTime + FIXED_OVERHEAD_SEC;

  setPathStats({ mmTotal, contours, layers: layerCount });
  setEstimatedCutTimeSec(total);
}, []);

  const loadModel = useCallback(() => {
    setIsLoading(true);
    setError(null);
    
    if (objRef.current) {
      sceneRef.current?.remove(objRef.current);
      objRef.current.geometry?.dispose();
      if (Array.isArray(objRef.current.material)) {
        objRef.current.material.forEach(m => m.dispose());
      } else {
        objRef.current.material?.dispose();
      }
      objRef.current = null;
    }
    
    removeVisualHelpers(sceneRef.current);
    const loader = new STLLoader();
    
    loader.load(stlFile || "/models/twistT.stl", (geometry) => {
      geometry.computeBoundingBox();
      const center = new THREE.Vector3();
      geometry.boundingBox.getCenter(center);
      geometry.translate(-center.x, -center.y, -center.z);
      geometry.rotateX(-Math.PI / 2);
      geometry.rotateY(-Math.PI / 2);
      
      const material = new THREE.MeshStandardMaterial({ 
        color: 0xd2a679, 
        metalness: 0.1, 
        roughness: 0.6, 
        transparent: true, 
        opacity: 0.85 
      });
      
      const mesh = new THREE.Mesh(geometry, material);

      // compute bbox BEFORE adding, set on ground, THEN add to scene once
      const bbox0 = new THREE.Box3().setFromObject(mesh);
      mesh.position.y = -bbox0.min.y;
      mesh.updateMatrixWorld(true);
      
      //sceneRef.current.add(mesh); // ← add after positioning
      mesh.updateMatrixWorld(true);
      
      // model dims (after placement)
      const bbox = new THREE.Box3().setFromObject(mesh);
      const size = bbox.getSize(new THREE.Vector3());
      setModelDimensions({ width: size.x, height: size.y, depth: size.z });
      
      // pre-check with the placed mesh
      const fit = checkLaserCutterConstraints(bbox);
      objRef.current = mesh; // keep a ref either way

      if (!fit.ok) {
        // show the model + warning, but skip heavy slicing
        setIsLoading(false);
        return;
      }
      

objRef.current = mesh;

  // 通过预检才进入昂贵的分层
  const horiz = generateHorizontalStackedLayers(mesh, sceneRef.current);
 // 估算：用刚刚填充的 shapesToExport + 层数
 const bboxAfter = new THREE.Box3().setFromObject(mesh);
 const sizeAfter = bboxAfter.getSize(new THREE.Vector3());
 const layerHeight = 3; // 这里与生成时保持一致
 const layerCount = Math.ceil(sizeAfter.y / layerHeight);
 estimateLaserTime(shapesToExport, layerCount);


  const group = new THREE.Group();
  horiz.extrudedMeshes.forEach(l => group.add(l.clone()));
  const box = new THREE.Box3().setFromObject(group);
  createVisualization(group, sceneRef.current, box);

  setIsLoading(false);
}, undefined, err => {
      console.error("Failed to load model", err);
      setError("Model loading failed");
      setIsLoading(false);
    });
  }, [stlFile, removeVisualHelpers, createVisualization, checkLaserCutterConstraints]);

  const handleDownloadSVG = useCallback(() => {
    if (!shapesToExport.length || !modelDimensions) return;
    
    // 在导出前检查约束
    if (!checkLaserCutterConstraints()) {
      console.warn("Some layers exceed laser cutter dimensions. Please review warnings before manufacturing.");
    }
  
    const svgData = generateSVG(
      shapesToExport,
      [],
      modelDimensions,
      3
    );
  
    downloadSVG(svgData, "stacked-layers2");
  }, [shapesToExport, modelDimensions, checkLaserCutterConstraints]);

  useEffect(() => {
    const cleanup = initializeThreeScene(containerRef, sceneRef, rendererRef, cameraRef, controlsRef);
    loadModel();
    return cleanup;
  }, [loadModel]);
  
  useEffect(() => {
    const handler = () => {

      checkLaserCutterConstraints();
    };
    window.addEventListener("labConstraints:updated", handler);
    return () => window.removeEventListener("labConstraints:updated", handler);
  }, [checkLaserCutterConstraints]);
  
  return (
    <div className="container">
      <div ref={containerRef} className="canvas-container"></div>


      {constraintWarnings.length > 0 && (
      <div style={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 10000
      }}>
        {constraintWarnings.map((w,i) => (
          <div key={i} style={{
            backgroundColor: '#ff9800',
            color: 'white',
            padding: '12px 16px',
            borderRadius: '6px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
            marginBottom: 8
          }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
              <span style={{ marginRight: 8 }}>⚠️</span>
              <strong>{w.layer || "Model"} Exceeds {w.cutter} Size</strong>
            </div>
            <div style={{ fontSize: 13 }}>
              Current: {w.currentSize.width} × {w.currentSize.height} mm
              <br />
              Laser Cutter Max: {w.maxSize.width} × {w.maxSize.height} mm
            </div>
          </div>
        ))}
      </div>
    )}
      <button
        className="panel-toggle-button"
        onClick={() => setExpanded((prev) => !prev)}
      >
        {expanded ? "⟨ Hide Panel" : "⟩ Show Panel"}
      </button>
  
      {/* Floating Control Panel */}
      {!isLoading && expanded && (
        <div className="controls-panel">
          <div className="panel-title">Stacked Sheets 2</div>
  
          {/* Information section */}
          <div className="panel-section">
            <div className="section-header">
              <h4>Workflow Description</h4>
            </div>
            <div className="section-content">
              <p>
                The <strong>Stacked Sheets</strong> workflow slices your model into flat
                horizontal layers, laser-cut from wood, MDF, or acrylic, and stacked to
                form the final 3D shape.
              </p>
              <p style={{ fontStyle: "italic" }}>
                Compared to interlocking laser-cut structures, stacked sheets provide
                continuous solid volume but use more material and require post-processing
                for assembly.
              </p>
            </div>
          </div>

          <div className="panel-section">
  <div className="section-header">
    <h4>Time Estimate</h4>
  </div>
  <div className="section-content">
    <p>
      Estimated laser cut time (rough): <strong>{formatTimeS(estimatedCutTimeSec)}</strong>
    </p>
    <p style={{ marginTop: 6, fontSize: 13, color: '#555' }}>
      Total cut length: <strong>{formatMm(pathStats.mmTotal)}</strong> ·
      Contours: <strong>{pathStats.contours}</strong> ·
      Layers: <strong>{pathStats.layers}</strong>
    </p>
    <p style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}>
      Note: assumes millimeter units and typical plywood settings. Actual time varies
      with material, power/speed profiles, kerf compensation, and path optimization.
    </p>
  </div>
</div>


          {/* Buttons */}
          <div className="buttons-container">
            <div className="material-switch">
              <button
                className={`material-button wood-btn ${materialKey === 'wood' ? 'selected' : ''}`}
                onClick={() => {
                  if (materialKey !== 'wood') {
                    setMaterialKey('wood');
                    applyMaterialToLayers(woodMaterial);
                  }
                }}
                disabled={isLoading}
              >
                ● Wood
              </button>

              <button
                className={`material-button acrylic-btn ${materialKey === 'acrylic' ? 'selected' : ''}`}
                onClick={() => {
                  if (materialKey !== 'acrylic') {
                    setMaterialKey('acrylic');
                    applyMaterialToLayers(acrylicMaterial);
                  }
                }}
                disabled={isLoading}
              >
                ● Acrylic
              </button>
            </div>

            <div className="material-caption">
              {materialKey === 'wood' ? 'Wood' : 'Acrylic'}
            </div>

{/*             <button
              onClick={handleDownloadSVG}
              className="action-button"
              disabled={!svgReady || isLoading}
            >
              DOWNLOAD SVG FILES
            </button> */}
            
            <button
              onClick={() => setTutorialVisible((prev) => !prev)}
              className="action-button"
            >
              {tutorialVisible
                ? "HIDE INTERACTIVE GUIDANCE"
                : "SHOW INTERACTIVE GUIDANCE"}
            </button>
          </div>
        </div>
      )}
  
      {/* Loading + status */}
      {isLoading && <div className="loading-box">Loading model...</div>}
      {processingStatus && <div className="loading-box">{processingStatus}</div>}
      {error && <div className="error-box">Error: {error}</div>}
  
      <TutorialWindow
        visible={tutorialVisible}
        onToggleVisibility={() => setTutorialVisible((prev) => !prev)}
        tutorialContent={stackedLayersTutorial}
      />
    </div>
  );  
};

export default StackedLaserCutLayers2;